/**
 * P0-PM-004: Analytics Service
 * Simple analytics abstraction for tracking BoB Marketplace events
 */

interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp?: number;
}

class Analytics {
  private userId?: string;
  private sessionId: string;
  private enabled: boolean;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.enabled = import.meta.env.PROD; // Only enable in production
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  identify(userId: string) {
    this.userId = userId;
  }

  track(name: string, properties?: Record<string, any>) {
    if (!this.enabled) {
      // Log to console in development
      console.log('[Analytics]', name, properties);
      return;
    }

    const event: AnalyticsEvent = {
      name,
      properties: {
        ...properties,
        userId: this.userId,
        sessionId: this.sessionId,
        timestamp: Date.now(),
        url: window.location.href,
        referrer: document.referrer,
      },
      timestamp: Date.now(),
    };

    // Send to backend (non-blocking)
    this.sendToBackend(event).catch((error) => {
      // Silently fail - don't break user experience
      console.warn('Analytics error:', error);
    });
  }

  private async sendToBackend(event: AnalyticsEvent) {
    try {
      await fetch('/api/analytics/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
        // Use keepalive for events sent during page unload
        keepalive: true,
      });
    } catch (error) {
      // Network errors are expected and acceptable
      console.warn('Analytics network error:', error);
    }
  }
}

export const analytics = new Analytics();
